// The Employee Score column on the Red Flags page (Governance › Red Flags).
//
// Read from the same stored monthly totals the Employee Score page's 6-month view and the
// Dashboard read (GET /scorecard/:projectId/:month/six-month), so the three never disagree.
// The column shows the score of the month BEFORE the period on screen (October's flags →
// September's score). When that month has no stored score it falls back to the newest
// earlier month that has one, and says which. No React and no network here, so it is
// tested on its own.

/** The slice of one six-month entry this reads. */
export interface SixMonthScoreEntry {
  employee_code: string;
  /** Stored total per month, keyed 'YYYY-MM-01'. A month with no stored row is absent. */
  totals: Record<string, number>;
  /** The page's own 6-month average (in-tenure months that have a row), or null. */
  avg: number | null;
  /** The employee's join month ('YYYY-MM-01'), or null when unknown. */
  dojMonth?: string | null;
}

export interface ScoreHistoryMonth {
  month: string;
  /** null when no score is stored for the month, or the person had not joined yet. */
  score: number | null;
  /** The month is before the person joined (the stored row is a 0.00 placeholder). */
  notJoined: boolean;
}

export interface EmployeeScoreSummary {
  /** The score the column shows: the score month's, else the newest earlier one. */
  latest: { month: string; score: number } | null;
  /** The six months, oldest → newest, ending at the score month. */
  history: ScoreHistoryMonth[];
  avg: number | null;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** The month before the one `ymd` (YYYY-MM-DD) falls in, as 'YYYY-MM-01'. */
export function scoreMonthFor(ymd: string): string | null {
  const m = /^(\d{4})-(\d{2})/.exec(ymd ?? '');
  if (!m) return null;
  let y = Number(m[1]);
  let mo = Number(m[2]) - 1;
  if (mo < 1) { mo = 12; y -= 1; }
  return `${y}-${String(mo).padStart(2, '0')}-01`;
}

/** 'YYYY-MM-01' → 'Sep 2026'. */
export const scoreMonthLabel = (month: string): string => {
  const y = month.slice(0, 4);
  const mo = Number(month.slice(5, 7));
  return mo >= 1 && mo <= 12 ? `${MONTHS[mo - 1]} ${y}` : month;
};

/** 'YYYY-MM-01' → 'Sep'. */
export const scoreMonthShort = (month: string): string => {
  const mo = Number(month.slice(5, 7));
  return mo >= 1 && mo <= 12 ? MONTHS[mo - 1] : month;
};

export const formatScore = (n: number): string => n.toFixed(2);

/**
 * One summary per employee code (trimmed). `months` is the response's window, oldest →
 * newest. A month before the join month is "not joined", whatever the stored row says:
 * the store writes a 0.00 placeholder there and it is not a score.
 */
export function buildEmployeeScoreIndex(
  months: string[],
  employees: SixMonthScoreEntry[],
): Map<string, EmployeeScoreSummary> {
  const window = [...(months ?? [])].map((m) => String(m).slice(0, 10)).sort();
  const index = new Map<string, EmployeeScoreSummary>();
  for (const e of employees ?? []) {
    const code = String(e?.employee_code ?? '').trim();
    if (!code) continue;
    const doj = e.dojMonth ? String(e.dojMonth).slice(0, 10) : null;
    const history: ScoreHistoryMonth[] = window.map((month) => {
      const notJoined = doj != null && month < doj;
      const v = e.totals?.[month];
      const score = !notJoined && typeof v === 'number' && Number.isFinite(v) ? v : null;
      return { month, score, notJoined };
    });
    let latest: EmployeeScoreSummary['latest'] = null;
    for (let i = history.length - 1; i >= 0; i -= 1) {
      const h = history[i];
      if (h.score != null) { latest = { month: h.month, score: h.score }; break; }
    }
    index.set(code, { latest, history, avg: typeof e.avg === 'number' ? e.avg : null });
  }
  return index;
}
